// Google Ads conversions (operator, 06.10.2026). Sent only when the Google
// tag is loaded, i.e. after the visitor accepted it in the cookie banner
// (window.gtag exists only then). Each conversion also goes out as a standard
// gtag event, so Google Ads can import it before any label exists; once the
// account has conversion actions, put their labels into ADS_CONVERSION_LABELS.

import { GOOGLE_ADS_ID } from "@/lib/config";
import { getAdsConsent, getStatsConsent } from "@/lib/consent";

export type AdsConversion = "booking" | "lead" | "contact";

/** Labels from Google Ads → Goals → Conversions (the part after "AW-…/"). */
export const ADS_CONVERSION_LABELS: Partial<Record<AdsConversion, string>> = {
  booking: "9zznCMjv_5MdEPmIkqJE", // Purchase, primary
  lead: "Y79ICMXv_5MdEPmIkqJE", // Submit lead form, primary
  contact: "6qpnCJfpiJMdEPmIkqJE", // WhatsApp/Telegram, secondary
};

const STANDARD_EVENT: Record<AdsConversion, string> = { booking: "purchase", lead: "generate_lead", contact: "contact" };

/** Package prices in EUR, as on /preise (lib/stripe.ts is server-only). */
export const PACKAGE_VALUE_EUR: Record<string, number> = { essential: 249.99, complete: 649.99, apostille: 149.99, gemeindewechsel: 149.99 };

type Opts = { value?: number; transactionId?: string; method?: string; label?: string };

export function adsConversion(kind: AdsConversion, opts: Opts = {}, tries = 0): void {
  if (typeof window === "undefined") return;
  // The tag may also run for statistics alone (11.10.2026): the Ads
  // conversion needs the Ads consent, the standard event either consent.
  const adsOk = getAdsConsent() === "granted";
  if (!adsOk && getStatsConsent() !== "granted") return;
  if (typeof window.gtag !== "function") {
    // A page can report before the tag component has set gtag up (effects of
    // the page run first): retry for a few seconds. Without consent gtag never
    // appears, so nothing is sent.
    if (tries < 8) window.setTimeout(() => adsConversion(kind, opts, tries + 1), 500);
    return;
  }
  // A booking counts once per browser session, even if the thank-you page reloads.
  if (kind === "booking" && opts.transactionId) {
    try {
      const key = `dw.ads.booking.${opts.transactionId}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      /* private mode */
    }
  }
  const money = opts.value != null ? { value: opts.value, currency: "EUR" } : {};
  window.gtag("event", STANDARD_EVENT[kind], { ...money, ...(opts.transactionId ? { transaction_id: opts.transactionId } : {}), ...(opts.method ? { method: opts.method } : {}), ...(opts.label ? { event_label: opts.label } : {}) });
  const label = ADS_CONVERSION_LABELS[kind];
  if (label && adsOk) window.gtag("event", "conversion", { send_to: `${GOOGLE_ADS_ID}/${label}`, ...money, ...(opts.transactionId ? { transaction_id: opts.transactionId } : {}) });
}
